import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useBattlePassFilteredDrops, } from 'root/src/client/v2/battlePass/features/BattlePassDetails/hooks/useBattlePassFilteredDrops';
import { DARES_PER_PAGE, DareStatus, DEFAULT_DARE_DIFFICULTY_LEVELS, DEFAULT_PLATFORMS, DropMnemonicIdFilter, } from 'root/src/client/v2/battlePass/constants';
import { useBattlePassActiveCreatorTier, useBattlePassAvailableDaresFilter, useBattlePassTiers, } from 'root/src/client/v2/battlePass/features/BattlePassDetails/hooks/useBattlePassDetails';
import { useRecordId } from 'root/src/client/v2/common/hooks/useRecordId';
import analyticsSendItem from 'root/src/client/logic/analytics/util/analyticsSendItem';
import { BATTLE_PASS_ANALYTIC_EVENTS } from 'root/src/shared/battlePass/battlePass.analytics';
const BattlePassDetailsContext = createContext(null);
export const useBattlePassDetailsContext = () => {
    const context = useContext(BattlePassDetailsContext);
    if (!context) {
        throw new Error('useBattlePassDetailsContext must be used within a BattlePassDetailsContextProvider');
    }
    return context;
};
export const BattlePassDetailsContextProvider = ({ children, mnemonicId }) => {
    const provider = useBattlePassDetailsContextProvider(mnemonicId);
    useEffect(() => {
        provider.resetFilters();
    }, []);
    return (<BattlePassDetailsContext.Provider value={provider}>
			{children}
		</BattlePassDetailsContext.Provider>);
};
const useBattlePassDetailsContextProvider = (mnemonicId) => {
    const [currentPage, setCurrentPage] = useState(1);
    const recordId = useRecordId();
    const [dropMnemonicIds, setDropMnemonicIds] = useState([DropMnemonicIdFilter.All]);
    const [dareStatus, setDareStatus] = useState(DareStatus.All);
    const [tier, setTier] = useState(null);
    const [platforms, setPlatforms] = useState(DEFAULT_PLATFORMS);
    const [dareDifficultyLevels, setDareDifficultyLevels] = useState(DEFAULT_DARE_DIFFICULTY_LEVELS);
    const resetFilters = () => {
        setDropMnemonicIds([DropMnemonicIdFilter.All]);
        setDareStatus(DareStatus.All);
        setTier(null);
        setPlatforms(DEFAULT_PLATFORMS);
        setDareDifficultyLevels(DEFAULT_DARE_DIFFICULTY_LEVELS);
    };
    useEffect(() => {
        resetFilters();
    }, [recordId]);
    return {
        mnemonicId,
        dropMnemonicIds,
        setDropMnemonicIds,
        dareStatus,
        setDareStatus,
        tier,
        setTier,
        platforms,
        setPlatforms,
        currentPage,
        setCurrentPage,
        dareDifficultyLevels,
        setDareDifficultyLevels,
        resetFilters,
    };
};
export const useBattlePassMnemonicId = () => {
    const { mnemonicId } = useBattlePassDetailsContext();
    return mnemonicId;
};
export const useBattlePassDropMnemonicId = () => {
    const { dropMnemonicIds, setDropMnemonicIds } = useBattlePassDetailsContext();
    const onDropMnemonicIds = (params) => {
        if (typeof params === 'string') {
            analyticsSendItem(BATTLE_PASS_ANALYTIC_EVENTS.DROP_CHANGE, params);
            setDropMnemonicIds([params]);
            return;
        }
        analyticsSendItem(BATTLE_PASS_ANALYTIC_EVENTS.DROP_CHANGE, params.mnemonicId);
        setDropMnemonicIds((prevMnemonicIds) => {
            if (params.mnemonicId === DropMnemonicIdFilter.All) {
                return [DropMnemonicIdFilter.All];
            }
            const filteredPrevMnemonicIds = prevMnemonicIds.filter(mnemonicId => mnemonicId !== DropMnemonicIdFilter.All);
            const isLast = prevMnemonicIds.length === 1;
            if (params.isActive) {
                if (isLast) {
                    return filteredPrevMnemonicIds;
                }
                return filteredPrevMnemonicIds.filter(mnemonicId => mnemonicId !== params.mnemonicId);
            }
            return [...filteredPrevMnemonicIds, params.mnemonicId];
        });
    };
    return { dropMnemonicIds, onDropMnemonicIds };
};
export const useBattlePassDaresStatus = () => {
    const { dareStatus, setDareStatus } = useBattlePassDetailsContext();
    return { dareStatus, setDareStatus };
};
export const useBattlePassTier = () => {
    var _a;
    const { tier, setTier } = useBattlePassDetailsContext();
    const { tiers } = useBattlePassTiers();
    const { creatorTier } = useBattlePassActiveCreatorTier();
    if (!tier) {
        const hasTier = tiers.some(item => item.tier === creatorTier);
        if (!hasTier) {
            return { tier: ((_a = tiers[0]) === null || _a === void 0 ? void 0 : _a.tier) || 1, setTier, activeCreatorTier: creatorTier };
        }
        return { tier: creatorTier, setTier, activeCreatorTier: creatorTier };
    }
    return { tier, setTier, activeCreatorTier: creatorTier };
};
export const useBattlePassDaresPlatforms = () => {
    const { availablePlatforms } = useBattlePassAvailableDaresFilter();
    const { platforms, setPlatforms } = useBattlePassDetailsContext();
    const onPlatforms = useOnDareSetter(setPlatforms, availablePlatforms);
    return { platforms, onPlatforms };
};
export const useBattlePassDaresDifficultyLevels = () => {
    const { availableLevels } = useBattlePassAvailableDaresFilter();
    const { dareDifficultyLevels, setDareDifficultyLevels } = useBattlePassDetailsContext();
    const onLevels = useOnDareSetter(setDareDifficultyLevels, availableLevels);
    return { levels: dareDifficultyLevels, onLevels };
};
const useOnDareSetter = (setter, items) => {
    const { setCurrentPage } = useBattlePassDetailsContext();
    return (params) => {
        setter((prevValues) => {
            if (prevValues.length >= items.length) {
                return [params.value];
            }
            if (params.isActive) {
                return items;
            }
            return [...prevValues, params.value];
        });
        setCurrentPage(1);
    };
};
export const useBattlePassDaresPagination = () => {
    const drops = useBattlePassFilteredDrops();
    const { currentPage, setCurrentPage } = useBattlePassDetailsContext();
    useEffect(() => {
        setCurrentPage(1);
    }, [drops]);
    const paginatedDares = useMemo(() => {
        const dares = drops.flatMap((drop) => {
            return drop.dares.map(dare => ({ dare, dropId: drop.id }));
        });
        return dares.slice((currentPage - 1) * DARES_PER_PAGE, (currentPage * DARES_PER_PAGE));
    }, [drops, currentPage]);
    const allPage = useMemo(() => {
        const allDaresLength = drops.reduce((accum, drop) => accum + drop.dares.length, 0);
        return Math.ceil(allDaresLength / DARES_PER_PAGE) || 1;
    }, [drops]);
    return { dares: paginatedDares, currentPage, setCurrentPage, allPage };
};
